// What the SHEET says a character resists, keyed by character and kept.
//
// The defence picker on a party card and on a tracker row was built to take a
// baseline plus the DM's own edits - effectiveDefenses(sheet, row) layers one
// over the other - but both callers passed sheet: null. Everything shown was
// therefore something the DM had typed, and a paladin's poison resistance or a
// barbarian's rage had to be entered by hand on a screen that already knew.
//
// It knew in the wrong place. The effects engine reads a token's defences
// through the connector (lmc-effects.js produces exactly {resist, immune,
// vulnerable}) and caches them in a React ref keyed by ROLL20 TOKEN ID, which
// dies with the page and means nothing to a character card. This moves that
// knowledge into the layout, keyed by CHARACTER, where both screens can see it
// and where it survives a reload.
//
// WHAT THIS IS NOT. It is not a read of the D&D Beyond character record - the
// connector's sheet reader does not extract defences at all. The baseline
// appears only once the effects lane has seen that character's token on a
// Roll20 map. No bridge, or no token, means no baseline and the screens behave
// exactly as they did before. That is a real limit, not a bug, and it is why
// this remembers what it learns instead of asking each time.
import type { Layout } from "./types";

/** The shape DefenseStrip and sheetEntries() already expect. */
export interface SheetDefenses { resist?: string; immune?: string; vulnerable?: string }

interface Remembered extends SheetDefenses { at: string; via: string }

const store = (layout: Layout): Record<string, Remembered> =>
  ((layout.sheetDefenses as Record<string, Remembered> | undefined) || {});

// Resolving a token to a character lives in roll20Bars.ts (characterForToken),
// which also matches by name when the id link is missing. This module does not
// ship a second, weaker copy of it.
const clean = (v: unknown): string => String(v ?? "").trim();
const hasAny = (d: SheetDefenses) => Boolean(clean(d.resist) || clean(d.immune) || clean(d.vulnerable));

/** The baseline for a character, or null when nothing has been learned.
 *
 *  Null rather than an empty object on purpose: effectiveDefenses treats a
 *  present-but-empty sheet as "this character demonstrably resists nothing",
 *  which is a different claim from "nobody has looked". */
export function sheetDefensesFor(layout: Layout, characterId: string): SheetDefenses | null {
  const d = store(layout)[characterId];
  if (!d || !hasAny(d)) return null;
  return { resist: clean(d.resist), immune: clean(d.immune), vulnerable: clean(d.vulnerable) };
}

/** Remember what the lane read. Returns the layout unchanged when there is
 *  nothing new to say, so this can be called on every read without churning
 *  the autosave. */
export function rememberSheetDefenses(layout: Layout, characterId: string, read: SheetDefenses & { unknown?: boolean }, via = "effects lane"): Layout {
  if (!characterId) return layout;
  // An "unknown" row means the sheet had not loaded yet - recording it would
  // cache the absence and stop anyone asking again.
  if (read.unknown) return layout;
  const next: Remembered = { resist: clean(read.resist), immune: clean(read.immune), vulnerable: clean(read.vulnerable), at: new Date().toISOString(), via };
  if (!hasAny(next)) return layout;
  const had = store(layout)[characterId];
  if (had && had.resist === next.resist && had.immune === next.immune && had.vulnerable === next.vulnerable) return layout;
  return { ...layout, sheetDefenses: { ...store(layout), [characterId]: next } };
}

/** Forget one character's baseline, for when a sheet changes and the DM wants it re-read. */
export function forgetSheetDefenses(layout: Layout, characterId: string): Layout {
  const cur = store(layout);
  if (!Object.prototype.hasOwnProperty.call(cur, characterId)) return layout;
  const next = { ...cur };
  delete next[characterId];
  return { ...layout, sheetDefenses: next };
}

/** When it was learned, for the tooltip - a baseline read three sessions ago is
 *  worth showing, but the DM should be able to tell. */
export function sheetDefensesAge(layout: Layout, characterId: string): string {
  const d = store(layout)[characterId];
  if (!d?.at) return "";
  const when = new Date(d.at);
  return Number.isNaN(when.getTime()) ? "" : when.toLocaleString();
}
